import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

/**
 * "Add to …" picker: serif title, hint, search + "select all", bordered
 * checkbox cards, Cancel / Add footer.
 *
 * Every candidate is listed; the ones already in place arrive pre-checked
 * (`initialSelectedIds`). Unticking one removes it on submit. The submit
 * button stays disabled until something actually changed.
 *
 * items: [{
 *   id, label,
 *   products?: [{ id, label }],
 *   children?: [{ id, label, products?: [{ id, label }] }],
 * }]
 *   `products` — ticking the item expands a panel to keep/hide those products.
 *   `children` — ticking the item expands a panel of sub-items (subcategories)
 *   each with its own keep switch and, when kept, a nested product panel.
 *
 * kept / initialKept: { [id]: string[] } — product ids kept; missing = all.
 *   Nested child products are keyed as `${parentId}:${childId}`.
 * keptChildren / initialKeptChildren: { [parentId]: childId[] }; missing = all.
 * onToggle(id) · onSetSelected(ids) · onKeptChange(itemId, keptIds)
 * onKeptChildrenChange(parentId, childIds)
 *
 * onCreate(name) → Promise<string | undefined>: optional. When provided, a
 * dashed "+ New …" button opens an inline input; the created item's id is
 * auto-checked once the promise resolves.
 */
const sameSet = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

const childProductKey = (parentId, childId) => `${parentId}:${childId}`;

const idsKept = (allIds, custom) =>
  Array.isArray(custom) ? allIds.filter((id) => custom.includes(id)) : allIds;

/** Shared product keep/hide list (avatar + switch). */
const ProductsKeepBlock = ({ products, keptIds, onKeptChange, idPrefix, t }) => {
  const total = products.length;
  const keptCount = keptIds.length;
  return (
    <>
      <div className="menu-picker__panel-head">
        <span className="menu-picker__panel-summary">
          <strong>{keptCount}</strong> {t("menuTreeKeptSummary", { total })}
        </span>
        <button
          type="button"
          className="btn menu-picker__panel-toggle"
          onClick={() => onKeptChange(keptCount === total ? [] : products.map((p) => p.id))}
        >
          {keptCount === total ? t("menuTreeUncheckAll") : t("menuTreeCheckAll")}
        </button>
      </div>
      <div
        className="menu-picker__progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={keptCount}
      >
        <span style={{ width: `${total ? (keptCount / total) * 100 : 0}%` }} />
      </div>
      <div className="menu-picker__products">
        {products.map((product) => {
          const on = keptIds.includes(product.id);
          const switchId = `${idPrefix}-${product.id}`;
          const initial = String(product.label || "?").trim().charAt(0).toUpperCase();
          return (
            <label key={product.id} htmlFor={switchId} className={`menu-picker__product ${on ? "is-on" : ""}`}>
              <span className="menu-picker__avatar" aria-hidden>
                {initial || "?"}
              </span>
              <span className="menu-picker__product-label">{product.label}</span>
              <input
                id={switchId}
                type="checkbox"
                className="menu-picker__switch"
                role="switch"
                checked={on}
                aria-checked={on}
                onChange={() =>
                  onKeptChange(on ? keptIds.filter((id) => id !== product.id) : [...keptIds, product.id])
                }
              />
            </label>
          );
        })}
      </div>
    </>
  );
};

const AssignPickerModal = ({
  show,
  onHide,
  title,
  subtitle,
  items,
  selectedIds,
  initialSelectedIds,
  onToggle,
  onSetSelected,
  kept,
  initialKept,
  onKeptChange,
  keptChildren,
  initialKeptChildren,
  onKeptChildrenChange,
  onSubmit,
  submitting = false,
  searchPlaceholder,
  onCreate,
  createLabel,
  createPlaceholder,
  className,
  childrenBadgeKey = "menuTreeSubcategoriesBadge",
  keptChildrenSummaryKey = "menuTreeKeptSubsSummary",
  t,
}) => {
  const [search, setSearch] = useState("");

  // ---- inline "create new" row
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  // ---- products panel open/closed per item (undefined = default)
  const [openPanels, setOpenPanels] = useState({});

  useEffect(() => {
    if (show) {
      setSearch("");
      setCreating(false);
      setNewName("");
      setCreateBusy(false);
      setOpenPanels({});
    }
  }, [show]);

  const togglePanel = (id, fallback) =>
    setOpenPanels((prev) => ({ ...prev, [id]: !(prev[id] ?? fallback) }));

  const cancelCreate = () => {
    if (createBusy) return;
    setCreating(false);
    setNewName("");
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!onCreate || !name || createBusy) return;
    setCreateBusy(true);
    try {
      const newId = await onCreate(name);
      if (newId && onSetSelected) {
        onSetSelected(Array.from(new Set([...(selectedIds || []), String(newId)])));
      }
      setCreating(false);
      setNewName("");
    } finally {
      setCreateBusy(false);
    }
  };

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const selected = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds]
  );
  const keptMap = kept || {};
  const childrenMap = keptChildren || {};

  /** Rows matching the search — "select all" acts on these. */
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => String(item.label || "").toLowerCase().includes(q));
  }, [list, search]);

  const allVisibleSelected =
    rows.length > 0 && rows.every((item) => selected.includes(item.id));

  const handleSelectAll = () => {
    if (!onSetSelected) return;
    const visibleIds = rows.map((item) => item.id);
    if (allVisibleSelected) {
      onSetSelected(selected.filter((id) => !visibleIds.includes(id)));
    } else {
      onSetSelected(Array.from(new Set([...selected, ...visibleIds])));
    }
  };

  const keptIdsFor = (item, map = keptMap) =>
    idsKept((item.products || []).map((p) => p.id), map[item.id]);

  const childIdsFor = (item, map = childrenMap) =>
    idsKept((item.children || []).map((c) => c.id), map[item.id]);

  const childProductIdsFor = (parent, child, map = keptMap) =>
    idsKept((child.products || []).map((p) => p.id), map[childProductKey(parent.id, child.id)]);

  // ---- diff vs. the initial state → drives the submit button
  const initial = Array.isArray(initialSelectedIds) ? initialSelectedIds : [];
  const addedCount = selected.filter((id) => !initial.includes(id)).length;
  const removedCount = initial.filter((id) => !selected.includes(id)).length;
  const keptChanged = list.some((item) => {
    if (!selected.includes(item.id) || !initial.includes(item.id)) return false;
    if (Array.isArray(item.products) && item.products.length > 0) {
      if (!sameSet(keptIdsFor(item), keptIdsFor(item, initialKept || {}))) return true;
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      if (!sameSet(childIdsFor(item), childIdsFor(item, initialKeptChildren || {}))) return true;
      return item.children.some(
        (child) =>
          Array.isArray(child.products) &&
          child.products.length > 0 &&
          !sameSet(
            childProductIdsFor(item, child),
            childProductIdsFor(item, child, initialKept || {})
          )
      );
    }
    return false;
  });
  const hasChanges = addedCount > 0 || removedCount > 0 || keptChanged;
  const canSubmit = hasChanges && !submitting;

  const submitLabel =
    removedCount > 0 || keptChanged
      ? t("saveChanges")
      : addedCount > 0
        ? `${t("menuTreeAddShort")} (${addedCount})`
        : t("menuTreeAddShort");

  return (
    <Modal
      show={show}
      onHide={() => !submitting && onHide()}
      centered
      scrollable
      enforceFocus={false}
      restoreFocus
      className={`fade menu-picker ${className || ""}`.trim()}
      dialogClassName="menu-picker__dialog"
      contentClassName="menu-picker__content"
    >
      <Modal.Body className="menu-picker__body">
        <h3 className="menu-picker__title">{title}</h3>
        {subtitle ? <p className="menu-picker__subtitle">{subtitle}</p> : null}

        <div className="menu-picker__tools">
          <label className="menu-picker__search">
            <i className="fas fa-search" aria-hidden />
            <input
              type="search"
              value={search}
              placeholder={searchPlaceholder || t("menuTreeSearchPlaceholder")}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={searchPlaceholder || t("menuTreeSearchPlaceholder")}
              autoFocus
            />
          </label>
          <button
            type="button"
            className={`btn menu-picker__select-all ${allVisibleSelected ? "is-active" : ""}`}
            onClick={handleSelectAll}
            disabled={rows.length === 0}
            aria-pressed={allVisibleSelected}
          >
            <span className="menu-picker__check" aria-hidden data-checked={allVisibleSelected} />
            {allVisibleSelected
              ? t("menuTreeDeselectAll")
              : `${t("menuTreeSelectAll")} (${rows.length})`}
          </button>
        </div>

        {onCreate ? (
          creating ? (
            <form
              className="menu-picker__create-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              <input
                type="text"
                className="menu-picker__create-input"
                value={newName}
                placeholder={createPlaceholder || t("menuTreeNewCategoryPlaceholder")}
                aria-label={createPlaceholder || t("menuTreeNewCategoryPlaceholder")}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") cancelCreate();
                }}
                disabled={createBusy}
                autoFocus
              />
              <button
                type="submit"
                className="btn menu-picker__create-submit"
                disabled={!newName.trim() || createBusy}
              >
                {createBusy ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                ) : (
                  t("menuTreeCreate")
                )}
              </button>
              <button
                type="button"
                className="btn menu-picker__create-cancel"
                onClick={cancelCreate}
                disabled={createBusy}
                aria-label={t("cancel")}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="btn menu-picker__create"
              onClick={() => setCreating(true)}
              disabled={submitting}
            >
              <span className="menu-picker__create-plus" aria-hidden>
                <i className="fas fa-plus" />
              </span>
              {createLabel || t("menuTreeNewCategory")}
            </button>
          )
        ) : null}

        <div className="menu-picker__list" role="list">
          {list.length === 0 ? (
            <p className="menu-picker__empty">{t("noDataFound")}</p>
          ) : rows.length === 0 ? (
            <p className="menu-picker__empty">{t("assignModalEmptyFilter")}</p>
          ) : (
            rows.map((item) => {
              const checked = selected.includes(item.id);
              const inputId = `menu-picker-${item.id}`;
              const products = Array.isArray(item.products) ? item.products : null;
              const children = Array.isArray(item.children) ? item.children : null;
              const productTotal = products ? products.length : 0;
              const childTotal = children ? children.length : 0;
              const keptIds = products ? keptIdsFor(item) : [];
              const keptChildIds = children ? childIdsFor(item) : [];
              const hasProductsPanel = checked && products && productTotal > 0;
              const hasChildrenPanel = checked && children && childTotal > 0;
              const hasPanel = hasProductsPanel || hasChildrenPanel;
              const wasInitial = initial.includes(item.id);
              const panelOpen = hasPanel && (openPanels[item.id] ?? !wasInitial);
              const panelId = `menu-picker-panel-${item.id}`;
              const badgeTotal = hasChildrenPanel || childTotal > 0 ? childTotal : productTotal;
              const badgeKept = hasChildrenPanel ? keptChildIds.length : keptIds.length;
              const showBadge = (products && productTotal > 0) || (children && childTotal > 0);

              return (
                <div
                  key={item.id}
                  className={`menu-picker__card ${checked ? "is-checked" : ""} ${
                    panelOpen ? "is-expanded" : ""
                  }`}
                  role="listitem"
                >
                  <div className="menu-picker__head">
                    <label htmlFor={inputId} className="menu-picker__row">
                      <input
                        id={inputId}
                        type="checkbox"
                        className="menu-picker__check"
                        checked={checked}
                        onChange={() => onToggle(item.id)}
                      />
                      <span className="menu-picker__label">{item.label}</span>
                      {showBadge && (
                        <span
                          className={`menu-picker__badge ${hasPanel ? "menu-picker__badge--kept" : ""}`}
                        >
                          {hasPanel
                            ? t("menuTreeKeptBadge", { kept: badgeKept, total: badgeTotal })
                            : children && childTotal > 0
                              ? t(childrenBadgeKey, { count: childTotal })
                              : t("menuTreeProductsBadge", { count: productTotal })}
                        </span>
                      )}
                    </label>
                    {hasPanel && (
                      <button
                        type="button"
                        className={`btn menu-picker__chevron ${panelOpen ? "is-open" : ""}`}
                        onClick={() => togglePanel(item.id, !wasInitial)}
                        aria-expanded={panelOpen}
                        aria-controls={panelId}
                        aria-label={panelOpen ? t("menuTreeCollapse") : t("menuTreeExpand")}
                      >
                        <i className="fas fa-chevron-down" aria-hidden />
                      </button>
                    )}
                  </div>

                  {panelOpen && hasProductsPanel && (
                    <div className="menu-picker__panel" id={panelId}>
                      <ProductsKeepBlock
                        products={products}
                        keptIds={keptIds}
                        idPrefix={`menu-picker-${item.id}`}
                        onKeptChange={(ids) => onKeptChange?.(item.id, ids)}
                        t={t}
                      />
                    </div>
                  )}

                  {panelOpen && hasChildrenPanel && (
                    <div className="menu-picker__panel" id={panelId}>
                      <div className="menu-picker__panel-head">
                        <span className="menu-picker__panel-summary">
                          <strong>{keptChildIds.length}</strong>{" "}
                          {t(keptChildrenSummaryKey, { total: childTotal })}
                        </span>
                        <button
                          type="button"
                          className="btn menu-picker__panel-toggle"
                          onClick={() =>
                            onKeptChildrenChange?.(
                              item.id,
                              keptChildIds.length === childTotal ? [] : children.map((c) => c.id)
                            )
                          }
                        >
                          {keptChildIds.length === childTotal
                            ? t("menuTreeUncheckAll")
                            : t("menuTreeCheckAll")}
                        </button>
                      </div>
                      <div
                        className="menu-picker__progress"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={childTotal}
                        aria-valuenow={keptChildIds.length}
                      >
                        <span
                          style={{
                            width: `${childTotal ? (keptChildIds.length / childTotal) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <div className="menu-picker__children">
                        {children.map((child) => {
                          const childOn = keptChildIds.includes(child.id);
                          const childProducts = Array.isArray(child.products) ? child.products : [];
                          const childProductKept = childProductIdsFor(item, child);
                          const switchId = `menu-picker-child-${item.id}-${child.id}`;
                          const childPanelKey = childProductKey(item.id, child.id);
                          const childHasPanel = childOn && childProducts.length > 0;
                          const childPanelOpen =
                            childHasPanel && (openPanels[childPanelKey] ?? true);
                          return (
                            <div
                              key={child.id}
                              className={`menu-picker__child ${childOn ? "is-on" : ""} ${
                                childPanelOpen ? "is-expanded" : ""
                              }`}
                            >
                              <div className="menu-picker__head">
                                <label
                                  htmlFor={switchId}
                                  className={`menu-picker__product ${childOn ? "is-on" : ""}`}
                                >
                                  <span className="menu-picker__avatar menu-picker__avatar--folder" aria-hidden>
                                    <i className="fas fa-folder" />
                                  </span>
                                  <span className="menu-picker__product-label">{child.label}</span>
                                  {childProducts.length > 0 && (
                                    <span
                                      className={`menu-picker__badge ${
                                        childOn ? "menu-picker__badge--kept" : ""
                                      }`}
                                    >
                                      {childOn
                                        ? t("menuTreeKeptBadge", {
                                            kept: childProductKept.length,
                                            total: childProducts.length,
                                          })
                                        : t("menuTreeProductsBadge", {
                                            count: childProducts.length,
                                          })}
                                    </span>
                                  )}
                                  <input
                                    id={switchId}
                                    type="checkbox"
                                    className="menu-picker__switch"
                                    role="switch"
                                    checked={childOn}
                                    aria-checked={childOn}
                                    onChange={() =>
                                      onKeptChildrenChange?.(
                                        item.id,
                                        childOn
                                          ? keptChildIds.filter((id) => id !== child.id)
                                          : [...keptChildIds, child.id]
                                      )
                                    }
                                  />
                                </label>
                                {childHasPanel && (
                                  <button
                                    type="button"
                                    className={`btn menu-picker__chevron ${childPanelOpen ? "is-open" : ""}`}
                                    onClick={() => togglePanel(childPanelKey, true)}
                                    aria-expanded={childPanelOpen}
                                    aria-label={
                                      childPanelOpen ? t("menuTreeCollapse") : t("menuTreeExpand")
                                    }
                                  >
                                    <i className="fas fa-chevron-down" aria-hidden />
                                  </button>
                                )}
                              </div>
                              {childPanelOpen && (
                                <div className="menu-picker__child-panel">
                                  <ProductsKeepBlock
                                    products={childProducts}
                                    keptIds={childProductKept}
                                    idPrefix={childPanelKey}
                                    onKeptChange={(ids) => onKeptChange?.(childPanelKey, ids)}
                                    t={t}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="menu-picker__footer">
        <button
          type="button"
          className="btn menu-picker__btn menu-picker__btn--cancel"
          onClick={onHide}
          disabled={submitting}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="btn menu-picker__btn menu-picker__btn--add"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {submitting ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
          ) : (
            submitLabel
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignPickerModal;
